import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ATTACHMENT_FIELDS } from '../constants/onlineForm';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const EXCLUDE_EXTERNAL_IDS = ['92817', '92129', '91702'];

export const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let decoded = text
    .replace(/&qt;/g, '&quot;')
    .replace(/&qt(?!;)/g, '&quot;')
    .replace(/&amp(?!;)/g, '&amp;')
    .replace(/&gt(?!;)/g, '&gt;')
    .replace(/&lt(?!;)/g, '&lt;')
    .replace(/&quot(?!;)/g, '&quot;')
    .replace(/&#39(?!;)/g, '&#39;')
    .replace(/&apos(?!;)/g, '&apos;');
  
  const textarea = document.createElement('textarea');
  textarea.innerHTML = decoded;
  return textarea.value;
};

export const convertToEuro = (num) => {
  let currency = Math.round(num / 100);

  if (typeof num === 'number') {
    currency = currency.toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
  }

  return currency;
};

export const convertFromCents = (num) => {
  if (typeof +num === 'number') return Math.round(+num / 100);

  return 0;
};

export const convertToDeDigits = (num) => {
  if (typeof num === 'number') {
    return num.toLocaleString('de-DE');
  }
  return num;
};

export const convertDate = (date) => {
  const dateObj = dayjs(date, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ']);

  const day = String(dateObj.utc(true).date()).length === 1
      ? `0${dateObj.utc(true).date()}`
      : dateObj.utc(true).date();
  const month = String(dateObj.month() + 1).length === 1
      ? `0${dateObj.month() + 1}`
      : dateObj.month() + 1;

  return `${day}.${month}.${dateObj.year()}`;
};

export const concatKpiValues = (obj1, obj2) => {

  if (obj1 && !obj2) return obj1;
  if (obj2 && !obj1) return obj2;
  if (!obj1 && !obj2) return null;

  return {
    branch_id: parseInt(obj1.branch_id, 10) + parseInt(obj2.branch_id, 10),
    donation_count: parseInt(obj1.donation_count, 10) + parseInt(obj2.donation_count, 10),
    project_count: parseInt(obj1.project_count, 10) + parseInt(obj2.project_count, 10),
    received_cents: parseInt(obj1.received_cents, 10) + parseInt(obj2.received_cents, 10)
  };
};

export const decodeFile = (blob, fileName, url) => {
  if (blob?.name) {
    return { fileName: blob.name, url: URL.createObjectURL(blob) };
  }
  if (url) {
    return { fileName, url };
  }
  return null;
};

export const decodeFiles = (blob, _fileName, url) => {
  const files = [];

  if (blob?.length) files.push(...blob.map(b => ({ fileName: b.name, url: URL.createObjectURL(b), isBlob: true })));
  if (url?.length) files.push(...url.flatMap(f => (f ? { fileName: f.split('/').at(-1), url: f, isBlob: false } : [])));

  return files;
};

export const fetchFormData = (values, application, actions = null) => {
  const formData = new FormData();
  const status = actions ? 1 : 0;

  for (const [key, value] of Object.entries(values)) {
    if (key === 'sponsoring_exclusive') {
      formData.append('sponsoring_exclusive_yes', value === 'yes' ? 1 : 0);
      formData.append('sponsoring_exclusive_no', value === 'no' ? 1 : 0);

      continue;
    }

    if (ATTACHMENT_FIELDS.includes(key) && !value && application[key.replace(/binary/, 'url')]) continue;

    if (key === 'organization_purpose_detailed') {
      if (value.length)
        value.forEach(item => formData.append('organization_purpose_detailed[]', item))
      else
        formData.append('organization_purpose_detailed', '')

      continue;
    }

    if (key === 'sdgs') {
      if (value.length)
        value.forEach(item => formData.append('sdgs[]', item))
      else
        formData.append('sdgs', '')

      continue;
    }

    if (key === 'attachment_binary' && value?.length) {
      value.forEach(item => formData.append('attachment_binary[]', item))
    } else if (key === 'attachment_binary' && !value?.length && application.attachment_url?.length) {
      continue;
    } else if (key === 'sponsoring_enabled') {
      continue;
    } else {
      formData.append(key, value);
    }
  }

  formData.append('status', status);

  return formData;
};

export const sanitizeHtmlContent = (html) => {
  if (!html || typeof html !== 'string') {
    return html;
  }

  const YOUTUBE_PERMISSIONS = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen';
  const VIDEO_PLATFORMS = /youtube|vimeo|betterplace/i;

  let sanitized = html.replace(
    /(src|href)="\/\/([^"]+)"/gi,
    (m, attr, url) => VIDEO_PLATFORMS.test(url) ? `${attr}="https://${url}"` : m
  );

  return sanitized.replace(/<iframe([^>]*?)>/gi, (match, attributes) => {
    if (!/src=["'][^"']*youtube\.com[^"']*["']/i.test(attributes)) {
      return match;
    }

    let updatedAttributes = attributes;

    updatedAttributes = updatedAttributes.replace(
      /src=["']([^"']*youtube\.com[^"']*)["']/i,
      (m, url) => `src="${url.startsWith('http') ? url : url.replace(/^\/\//, 'https://')}"`
    );

    const attrs = {
      frameborder: /frameborder=["']/i.test(updatedAttributes),
      style: /style=["']/i.test(updatedAttributes),
      allow: /allow=["']/i.test(updatedAttributes),
      allowfullscreen: /allowfullscreen/i.test(updatedAttributes),
      title: /title=["']/i.test(updatedAttributes),
      referrerpolicy: /referrerpolicy=["']/i.test(updatedAttributes),
      loading: /loading=["']/i.test(updatedAttributes)
    };

    if (attrs.frameborder) {
      updatedAttributes = updatedAttributes.replace(/frameborder=["'][^"']*["']/gi, '');
      if (attrs.style) {
        updatedAttributes = updatedAttributes.replace(
          /style=["']([^"']*)["']/i,
          (m, style) => style.includes('border') ? m : `style="${style}; border: 0;"`
        );
      } else {
        updatedAttributes += ' style="border: 0;"';
      }
    }

    if (attrs.allow) {
      updatedAttributes = updatedAttributes.replace(
        /allow=["']([^"']*)["']/i,
        (m, value) => value.includes('fullscreen') ? m : `allow="${value}; fullscreen"`
      );
    } else {
      updatedAttributes += ` allow="${YOUTUBE_PERMISSIONS}"`;
    }

    const additions = [];
    if (!attrs.allowfullscreen) additions.push('allowfullscreen');
    if (!attrs.title) additions.push('title="YouTube video player"');
    if (!attrs.referrerpolicy) additions.push('referrerpolicy="no-referrer-when-downgrade"');
    if (!attrs.loading) additions.push('loading="lazy"');

    if (additions.length > 0) {
      updatedAttributes += ' ' + additions.join(' ');
    }

    return `<iframe${updatedAttributes}>`;
  });
};